import React from 'react';
import { Skull, Cloud, AlertTriangle, Circle, Rainbow, MinusCircle, Target, Baby, Sparkles, Clock } from 'lucide-react';

const UpagrahasDisplay = ({ upagrahasData }) => {
  if (!upagrahasData) return null;

  const fixedUpagrahas = upagrahasData["6.1_fixed_upagrahas"] || {};
  const specialSphutas = upagrahasData["6.2_special_sphutas"] || {};

  // Icon mapping for Upagrahas
  const upagrahaIcons = {
    "Gulika": Skull,
    "Mrityu": Skull,
    "Dhuma": Cloud,
    "Vyatipata": AlertTriangle,
    "Parivesa": Circle,
    "Indrachapa": Rainbow,
    "Upaketu": MinusCircle
  };

  const upagrahaOrder = ["Gulika", "Mrityu", "Dhuma", "Vyatipata", "Parivesa", "Indrachapa", "Upaketu"];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-500 mb-2">
          Upagrahas & Sphutas
        </h2>
        <p className="text-white/70 text-sm">
          Shadow Points for Health, Accidents, Spiritual Patterns & Karmic Timing
        </p>
      </div>

      {/* Fixed Upagrahas */}
      <div>
        <h3 className="text-red-400 text-2xl font-bold mb-4">Fixed Upagrahas (Sons of Planets)</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-red-900/30 border-b border-red-700/50">
                <th className="text-left p-3 text-red-300">Name</th>
                <th className="text-left p-3 text-red-300">Ruling Planet</th>
                <th className="text-left p-3 text-red-300">Longitude</th>
                <th className="text-left p-3 text-red-300">Sign</th>
                <th className="text-left p-3 text-red-300">Nakṣatra</th>
                <th className="text-center p-3 text-red-300">Pada</th>
                <th className="text-center p-3 text-red-300">House</th>
                <th className="text-left p-3 text-red-300">Sign Lord</th>
                <th className="text-left p-3 text-red-300">Nak Lord</th>
                <th className="text-left p-3 text-red-300">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {upagrahaOrder.map((name) => {
                const upagraha = fixedUpagrahas[name];
                if (!upagraha) return null;

                const Icon = upagrahaIcons[name];

                return (
                  <tr key={name} className="border-b border-white/10 hover:bg-white/5">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {Icon && <Icon className="w-4 h-4 text-red-400" />}
                        <span className="text-yellow-400 font-bold">{name}</span>
                      </div>
                    </td>
                    <td className="p-3 text-cyan-300">{upagraha.ruling_planet}</td>
                    <td className="p-3 text-purple-300">{upagraha.longitude?.toFixed(2)}°</td>
                    <td className="p-3 text-green-300 font-medium">{upagraha.sign}</td>
                    <td className="p-3 text-blue-300">{upagraha.nakshatra}</td>
                    <td className="p-3 text-center text-pink-300">{upagraha.pada}</td>
                    <td className="p-3 text-center text-yellow-300 font-bold">{upagraha.house}</td>
                    <td className="p-3 text-indigo-300">{upagraha.sign_lord}</td>
                    <td className="p-3 text-teal-300">{upagraha.nakshatra_lord}</td>
                    <td className="p-3 text-white/60 text-xs italic">{upagraha.remarks}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Special Sphutas */}
      <div>
        <h3 className="text-purple-400 text-2xl font-bold mb-4">Special Sphutas</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Beeja/Kshetra */}
          {specialSphutas.beeja_kshetra && (
            <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 rounded-xl p-6 border border-green-500/30">
              <h4 className="text-green-400 text-xl font-bold mb-4 flex items-center gap-2">
                <Baby className="w-5 h-5" /> Bīja & Kṣetra (Fertility Points)
              </h4>
              
              <div className="space-y-4">
                {/* Beeja */}
                <div className="bg-white/5 rounded-lg p-4">
                  <h5 className="text-cyan-400 font-bold mb-2">🌱 Bīja (Seed - Male Fertility)</h5>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-white/60">Sign:</span>
                      <span className="text-yellow-400 ml-2 font-medium">{specialSphutas.beeja_kshetra.beeja.sign}</span>
                    </div>
                    <div>
                      <span className="text-white/60">House:</span>
                      <span className="text-cyan-400 ml-2 font-medium">{specialSphutas.beeja_kshetra.beeja.house}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-white/60">Nakṣatra:</span>
                      <span className="text-purple-400 ml-2">{specialSphutas.beeja_kshetra.beeja.nakshatra}</span>
                    </div>
                  </div>
                  <p className="text-white/50 text-xs mt-2 italic">{specialSphutas.beeja_kshetra.beeja.interpretation}</p>
                </div>

                {/* Kshetra */}
                <div className="bg-white/5 rounded-lg p-4">
                  <h5 className="text-pink-400 font-bold mb-2">🌸 Kṣetra (Field - Female Fertility)</h5>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-white/60">Sign:</span>
                      <span className="text-yellow-400 ml-2 font-medium">{specialSphutas.beeja_kshetra.kshetra.sign}</span>
                    </div>
                    <div>
                      <span className="text-white/60">House:</span>
                      <span className="text-cyan-400 ml-2 font-medium">{specialSphutas.beeja_kshetra.kshetra.house}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-white/60">Nakṣatra:</span>
                      <span className="text-purple-400 ml-2">{specialSphutas.beeja_kshetra.kshetra.nakshatra}</span>
                    </div>
                  </div>
                  <p className="text-white/50 text-xs mt-2 italic">{specialSphutas.beeja_kshetra.kshetra.interpretation}</p>
                </div>
              </div>
            </div>
          )}

          {/* 22nd Drekkana */}
          {specialSphutas["22nd_drekkana"] && (
            <div className="bg-gradient-to-br from-red-900/30 to-orange-900/30 rounded-xl p-6 border border-red-500/30">
              <h4 className="text-red-400 text-xl font-bold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" /> 22nd Drekkāṇa (Danger Point)
              </h4>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-white/60 text-xs mb-1">Sign</p>
                    <p className="text-yellow-400 font-bold">{specialSphutas["22nd_drekkana"].sign}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-white/60 text-xs mb-1">Longitude</p>
                    <p className="text-purple-400 font-bold">{specialSphutas["22nd_drekkana"].longitude?.toFixed(2)}°</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 col-span-2">
                    <p className="text-white/60 text-xs mb-1">Nakṣatra</p>
                    <p className="text-cyan-400 font-medium">{specialSphutas["22nd_drekkana"].nakshatra} (Pada {specialSphutas["22nd_drekkana"].pada})</p>
                  </div>
                </div>
                <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/30">
                  <p className="text-white/70 text-sm">{specialSphutas["22nd_drekkana"].interpretation}</p>
                </div>
              </div>
            </div>
          )}

          {/* 64th Navamsa */}
          {specialSphutas["64th_navamsa"] && (
            <div className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-xl p-6 border border-indigo-500/30">
              <h4 className="text-indigo-400 text-xl font-bold mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5" /> 64th Navāṁśa (Karmic Point)
              </h4>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-white/60 text-xs mb-1">Sign</p>
                    <p className="text-yellow-400 font-bold">{specialSphutas["64th_navamsa"].sign}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-white/60 text-xs mb-1">Longitude</p>
                    <p className="text-purple-400 font-bold">{specialSphutas["64th_navamsa"].longitude?.toFixed(2)}°</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 col-span-2">
                    <p className="text-white/60 text-xs mb-1">Nakṣatra</p>
                    <p className="text-cyan-400 font-medium">{specialSphutas["64th_navamsa"].nakshatra} (Pada {specialSphutas["64th_navamsa"].pada})</p>
                  </div>
                </div>
                <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/30">
                  <p className="text-white/70 text-sm">{specialSphutas["64th_navamsa"].interpretation}</p>
                </div>
              </div>
            </div>
          )}

          {/* Mrityu Sphuta */}
          {specialSphutas.mrityu_sphuta && (
            <div className="bg-gradient-to-br from-gray-900/30 to-slate-900/30 rounded-xl p-6 border border-gray-500/30">
              <h4 className="text-gray-400 text-xl font-bold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" /> Mrityu Sphuṭa (Longevity Point)
              </h4>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-white/60 text-xs mb-1">Sign</p>
                    <p className="text-yellow-400 font-bold">{specialSphutas.mrityu_sphuta.sign}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-white/60 text-xs mb-1">House</p>
                    <p className="text-cyan-400 font-bold">{specialSphutas.mrityu_sphuta.house}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-white/60 text-xs mb-1">Longitude</p>
                    <p className="text-purple-400 font-bold">{specialSphutas.mrityu_sphuta.longitude?.toFixed(2)}°</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-white/60 text-xs mb-1">Nakṣatra</p>
                    <p className="text-cyan-400 font-medium">{specialSphutas.mrityu_sphuta.nakshatra}</p>
                  </div>
                </div>
                <div className="bg-gray-500/10 rounded-lg p-3 border border-gray-500/30">
                  <p className="text-white/70 text-sm">{specialSphutas.mrityu_sphuta.interpretation}</p>
                </div>
              </div>
            </div>
          )}

          {/* Sphuta Drekkana */}
          {specialSphutas.sphuta_drekkana && (
            <div className="bg-gradient-to-br from-amber-900/30 to-yellow-900/30 rounded-xl p-6 border border-amber-500/30">
              <h4 className="text-amber-400 text-xl font-bold mb-4 flex items-center gap-2">
                <Target className="w-5 h-5" /> Sphuṭa Drekkāṇa (Precise Ascendant Sub-division)
              </h4>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-white/60 text-xs mb-1">Sign</p>
                    <p className="text-yellow-400 font-bold">{specialSphutas.sphuta_drekkana.sign}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-white/60 text-xs mb-1">Drekkāṇa</p>
                    <p className="text-cyan-400 font-bold">{specialSphutas.sphuta_drekkana.drekkana_number}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-white/60 text-xs mb-1">Drekkāṇa Lord</p>
                    <p className="text-purple-400 font-bold">{specialSphutas.sphuta_drekkana.drekkana_lord}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-white/60 text-xs mb-1">Nakṣatra</p>
                    <p className="text-cyan-400 font-medium">{specialSphutas.sphuta_drekkana.nakshatra} (Pada {specialSphutas.sphuta_drekkana.pada})</p>
                  </div>
                </div>
                <div className="bg-amber-500/10 rounded-lg p-3 border border-amber-500/30">
                  <p className="text-white/70 text-sm">{specialSphutas.sphuta_drekkana.interpretation}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Usage Guide */}
      <div className="bg-gradient-to-br from-slate-900/50 to-slate-800/30 rounded-xl p-6 border border-white/10">
        <h3 className="text-white text-lg font-bold mb-4">🔮 How to Use Upagrahas & Sphutas</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-white/70">
          <div className="flex items-start gap-2">
            <span className="text-red-400">•</span>
            <p><span className="text-cyan-400 font-medium">Gulika & Mṛtyu:</span> Saturn's sons - check for malefic influence in their houses</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-red-400">•</span>
            <p><span className="text-cyan-400 font-medium">Dhūma family:</span> Hidden obstacles, sudden events in those houses</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-green-400">•</span>
            <p><span className="text-cyan-400 font-medium">Bīja/Kṣetra:</span> Analyze for progeny and fertility timing</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-orange-400">•</span>
            <p><span className="text-cyan-400 font-medium">22nd Drekkāṇa:</span> Avoid major activities during transits to this point</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-purple-400">•</span>
            <p><span className="text-cyan-400 font-medium">64th Navāṁśa:</span> Past life karma manifestation point</p>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-gray-400">•</span>
            <p><span className="text-cyan-400 font-medium">Mrityu Sphuṭa:</span> Longevity assessment - malefics here reduce lifespan</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpagrahasDisplay;
